import { Loading } from "@dgs/core";
import { FC, useEffect } from "react";
import { Route, Routes, useParams } from "react-router";
import { NavigateWithQueryParams } from "../NavigateWithQueryParams";
import { useRegistrationFormDetailsQuery } from "../shared/api/registrationForms";
import { useConfig } from "../shared/hooks/useConfig";
import { useRegistrationToken } from "../shared/hooks/useRegistrationToken";
import {
	isRegistrationFormClosed,
	isRegistrationFormPaymentOutstanding,
	isRegistrationFormPrivate,
} from "../shared/types";
import { useNavigateWithQueryParams } from "../useNavigateWithQueryParams";
import { EmailGatePage } from "./email-gate/EmailGatePage";
import { RegistrationFormAvailablePage } from "./RegistrationFormAvailablePage";
import { RegistrationFormCodePage } from "./RegistrationFormCodePage";
import { RegistrationFormPaymentOutstandingPage } from "./RegistrationFormPaymentOutstandingPage";
import { RegistrationFormPrivatePage } from "./RegistrationFormPrivatePage";
import { RegistrationPreviewPage } from "./RegistrationPreviewPage";
import { RegistrationClosedPage } from "./staticPages";
import { RegistrationPaymentCallbackPage } from "./staticPages/RegistrationPaymentCallbackPage";

export const RegistrationPage: FC = () => {
	return (
		<Routes>
			<Route index element={<RedirectToCachedOrDefaultRegistration />} />
			<Route path="/:registrationId/*">
				<Route index element={<NavigateWithQueryParams to="landing-page" replace />} />
				<Route path="payment-callback/success" element={<RegistrationPaymentCallbackPage reason="success" />} />
				<Route path="payment-callback/cancel" element={<RegistrationPaymentCallbackPage reason="cancel" />} />
				<Route path="*" element={<RegistrationContentPage />} />
			</Route>
			<Route path="/preview/:registrationId/*">
				<Route index element={<NavigateWithQueryParams to="landing-page" replace />} />
				<Route path="*" element={<RegistrationPreviewPage />} />
			</Route>
		</Routes>
	);
};

export const RedirectToCachedOrDefaultRegistration: FC = () => {
	const navigate = useNavigateWithQueryParams();
	const registrationFormId = sessionStorage.getItem("registrationFormId");
	const {
		productData: { defaultRegistrationFormId },
	} = useConfig();

	useEffect(() => {
		if (registrationFormId) {
			navigate(registrationFormId);
		} else {
			defaultRegistrationFormId ? navigate(defaultRegistrationFormId.toString()) : navigate("..");
		}
	}, [defaultRegistrationFormId, navigate, registrationFormId]);

	return null;
};

export const RegistrationContentPage: FC = () => {
	const { registrationId = "" } = useParams<"registrationId">();
	const token = useRegistrationToken();
	const { data: registrationForm, isLoading } = useRegistrationFormDetailsQuery(registrationId, token);

	useEffect(() => {
		if (registrationForm) {
			sessionStorage.setItem("registrationFormId", registrationForm.id.toString());
		}
	}, [registrationForm]);

	if (isLoading || !registrationForm) {
		return <Loading />;
	}

	if (isRegistrationFormPrivate(registrationForm)) {
		if (registrationForm.isCodePageEnabled && !token) {
			return <RegistrationFormCodePage registrationForm={registrationForm} block={registrationForm.codePageBlock} />;
		}
		return <RegistrationFormPrivatePage block={registrationForm.privateBlock} />;
	} else if (isRegistrationFormClosed(registrationForm)) {
		return <RegistrationClosedPage block={registrationForm.closedBlock} />;
	} else if (isRegistrationFormPaymentOutstanding(registrationForm)) {
		return <RegistrationFormPaymentOutstandingPage paymentLink={registrationForm.paymentLink} />;
	} else if (registrationForm.isEmailGateEnabled && !token) {
		return <EmailGatePage registrationForm={registrationForm} />;
	}

	return <RegistrationFormAvailablePage registrationForm={registrationForm} />;
};
